<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSeo } from '@/composables/useSeo'
import SagaWorld from '@/components/philosophy/SagaWorld.vue'
import AshField from '@/components/philosophy/AshField.vue'

useSeo('philosophy')

/**
 * Philosophy — a scroll-driven visual novel: the journey from chaos
 * to connection, travelled across one continuous engraved landscape.
 *
 * The sticky backdrop (SagaWorld + skies + fog + ash canvas) lives
 * through the whole story; the chapters scroll over it. One master
 * GSAP timeline, scrubbed across the page, is the camera and the
 * weather at once:
 *
 *   prologue   a cold silent world, the bridge in ruins
 *   I    myth      the mythic Bifrost appears in the sky, a memory
 *   II   fracture  the canyon widens, stones fall, lights can't reach
 *   ~    dawn      warmth rises behind the ridge — never announced
 *   III  connexion the span rebuilds stone by stone, the tree outgrows
 *                  the mountain, people cross, the world ripples
 *   ~    climb     the camera rises past the clouds — silence
 *   IV   Heimdall  an ancient observatory opens its eye; rays of
 *                  understanding spread across everything visited
 *   end  pull-back the whole journey recomposes into one connected
 *                  landscape behind the final line
 *
 * GSAP + ScrollTrigger + MotionPath are imported in onMounted
 * (SSG-safe) and fully reverted in onUnmounted. The markup authors the
 * cold prologue, so the first SSG paint is already in-world; under
 * prefers-reduced-motion a single set() batch composes the final,
 * fully-connected wide shot instead of animating.
 */
const { locale } = useI18n()
const localPath = (path) => `/${locale.value}${path}`

const wrapRef = ref(null)
const ashRef = ref(null)

// chapter waypoints — each carved with its rune:
// Ansuz (the word), Hagalaz (the hail), Ehwaz (the two moving as one),
// Algiz (the watchful guardian)
const RUNES = [
  ['M8 2 L8 30', 'M8 7 L21 15', 'M8 15 L21 23'],
  ['M7 2 L7 30', 'M21 2 L21 30', 'M7 12 L21 20'],
  ['M6 30 L6 4', 'M22 30 L22 4', 'M6 4 L14 15 L22 4'],
  ['M14 30 L14 4', 'M14 13 L5 3', 'M14 13 L23 3']
]
// pacing: the empty space before each chapter is the breath between acts
const CHAPTERS = [
  { align: 'center', spacer: 'h-[20vh]', minH: 'min-h-[160vh]' },
  { align: 'start', spacer: 'h-[30vh]', minH: 'min-h-[170vh]' },
  { align: 'end', spacer: 'h-[60vh]', minH: 'min-h-[220vh]' },
  { align: 'start', spacer: 'h-[120vh]', minH: 'min-h-[180vh]' }
]

let gsapCtx = null

onMounted(async () => {
  const [{ gsap }, { ScrollTrigger }, { MotionPathPlugin }] = await Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger'),
    import('gsap/MotionPathPlugin')
  ])
  gsap.registerPlugin(ScrollTrigger, MotionPathPlugin)

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  gsapCtx = gsap.context((self) => {
    const stones = gsap.utils.toArray('.stone')
    // every rotation in the saga turns around its own anchor
    stones.forEach((el) => gsap.set(el, { svgOrigin: `${el.dataset.cx} ${el.dataset.cy}` }))
    gsap.set('#world', { svgOrigin: '720 430' })
    gsap.set('#cliff-left', { svgOrigin: '320 900' })
    gsap.set('#cliff-right', { svgOrigin: '1150 900' })
    gsap.set('#ridge-mid, #fg', { svgOrigin: '720 900' })
    gsap.set(['#ring-outer', '#ring-rete', '#iris', '#eye-core'], { svgOrigin: '1095 120' })

    if (reducedMotion) {
      setFinalState(gsap)
      return
    }

    ashRef.value?.start()

    // prologue: the tree is a seed behind the mountain, life is folded
    gsap.set('#tree', { svgOrigin: '345 560', scale: 0.82 })
    gsap.set('.t-leaf', { transformOrigin: 'left center', scale: 0.3 })
    gsap.set('.blade', { transformOrigin: '50% 100%', scaleY: 0.12, opacity: 0 })

    // ---- chapter copy emerges from the mist -------------------------
    gsap.utils.toArray('.story-block').forEach((block) => {
      gsap.fromTo(
        block.querySelectorAll('.reveal'),
        { opacity: 0, y: 30, filter: 'blur(10px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 1.1,
          stagger: 0.14,
          ease: 'power2.out',
          scrollTrigger: { trigger: block, start: 'top 68%', toggleActions: 'play none none reverse' }
        }
      )
    })

    // ---- the master timeline: 100 units of journey ------------------
    const mood = { v: 0 }
    const breathe = () => ashRef.value?.setMood(mood.v)

    const tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: wrapRef.value,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1.1
      }
    })

    // — prologue (0–8): the cold world barely stirs
    tl.to('#cliff-left, #cliff-right', { scale: 1.02, duration: 8 }, 0)
      .to('#fg', { scale: 1.04, duration: 8 }, 0)
      .to('#scroll-hint', { opacity: 0, duration: 2 }, 3)

    // — chapter I (10–23): the mythic bridge, drawn and dissolved
    tl.to('#myth', { opacity: 1, duration: 1.5 }, 10)
      .to('#myth-arc-glow, #myth-arc', { strokeDashoffset: 0, duration: 5 }, 10.5)
      .to('#myth-arc', { keyframes: { opacity: [1, 0.5, 0.9, 0.45, 0.8] }, duration: 2.5 }, 16)
      .to('#myth', { opacity: 0, duration: 3.5 }, 19)

    // — chapter II (26–39): the fracture
    tl.to('#cliff-left', { x: -46, duration: 8, ease: 'power1.inOut' }, 26)
      .to('#cliff-right', { x: 46, duration: 8, ease: 'power1.inOut' }, 26)
      .to('#abyss', { attr: { x: 500, width: 480 }, duration: 8 }, 26)
      .to('#story-dim', { opacity: 0.4, duration: 5 }, 27)
      .to('#fog-a', { opacity: 0.95, duration: 5 }, 27)
      .to('.shard', { x: (i, el) => +el.dataset.fx, y: (i, el) => +el.dataset.fy, duration: 8 }, 27)
      .to('#far-lights', { opacity: 0.9, duration: 2.5 }, 28.5)
      .to('#light-left, #light-right', { opacity: 1, duration: 3 }, 30)
      // the camera keeps advancing into the valley
      .to('#cliff-left, #cliff-right', { scale: 1.07, duration: 12 }, 26)
      .to('#ridge-mid', { scale: 1.03, duration: 12 }, 26)
      .to('#fg', { scale: 1.12, duration: 12 }, 26)
    // stones give way one after another, pulled into the abyss
    stones.forEach((el, i) => {
      tl.to(
        el,
        {
          x: gsap.getProperty(el, 'x') + +el.dataset.fx,
          y: gsap.getProperty(el, 'y') + +el.dataset.fy,
          rotation: gsap.getProperty(el, 'rotation') + +el.dataset.fr,
          duration: 6.5,
          ease: 'power1.in'
        },
        27 + i * 0.4
      )
    })

    // — the dawn nobody notices (39.5–45)
    tl.to('#sky-warm', { opacity: 1, duration: 5.5 }, 39.5)
      .to('#dawn-glow', { opacity: 0.55, duration: 4.5 }, 40)
      .to('#story-dim', { opacity: 0, duration: 4 }, 40)
      .to('#fog-a', { opacity: 0.45, duration: 5 }, 40)
      .to('#fog-b', { opacity: 0.35, duration: 5 }, 40)
      .to(mood, { v: 1, duration: 6, onUpdate: breathe }, 39.5)

    // — chapter III (45–63): the world heals, stone by stone
    tl.to('#cliff-left, #cliff-right', { x: 0, duration: 5, ease: 'power1.inOut' }, 45)
      .to('#cliff-left, #cliff-right, #ridge-mid, #fg', { scale: 1, duration: 8, ease: 'power1.inOut' }, 45)
      .to('#abyss', { attr: { x: 540, width: 400 }, duration: 5 }, 45)
      .to('.shard', { x: 0, y: 0, opacity: 0, duration: 4 }, 46)
      .to(
        '.stone',
        { x: 0, y: 0, rotation: 0, duration: 9, ease: 'power2.inOut', stagger: { each: 0.5, from: 'edges' } },
        46.5
      )
      .to('#tree .t-draw', { strokeDashoffset: 0, duration: 4, stagger: 0.12, ease: 'power1.inOut' }, 47.5)
      .to('#tree', { scale: 1, duration: 12, ease: 'power1.inOut' }, 50)
      .to(mood, { v: 1.15, duration: 4, onUpdate: breathe }, 50)
      .to('.t-leaf', { opacity: 1, scale: 1, duration: 2.5, stagger: 0.06, ease: 'power1.out' }, 54)
      .to('#dawn-glow', { opacity: 0.3, duration: 6 }, 56)
      .to('#deck-path', { strokeDashoffset: 0, duration: 3.5 }, 56.5)
      .to('.blade', { scaleY: 1, opacity: 1, duration: 3, stagger: 0.18 }, 57)
      .to('#light-left, #light-right', { opacity: 0, duration: 2 }, 58.5)
      .to('#birds', { opacity: 0.9, duration: 2 }, 59)
      .to('#deck-glow', { opacity: 0.8, duration: 2.5 }, 59.5)
      .to('#lanterns', { opacity: 1, duration: 1.5 }, 60)
      .to('#travelers', { opacity: 1, duration: 1.5 }, 60.5)
      .to('#flows', { opacity: 1, duration: 2 }, 60.5)
      .to('#ripples', { opacity: 1, duration: 1 }, 61)
      .to('#village-lights', { opacity: 1, duration: 2 }, 61.5)

    // — the climb (63–73): higher, past the clouds, into the night
    tl.to('#ridge-far', { y: 120, duration: 9 }, 63)
      .to('#ridge-mid', { y: 220, duration: 9 }, 63)
      .to('#cliff-left, #cliff-right, #bridge', { y: 520, duration: 9 }, 63)
      .to('#tree', { y: 620, duration: 9 }, 63)
      .to('#fg', { y: 660, duration: 9 }, 63)
      .to('#summit', { y: 0, duration: 9 }, 63)
      .to('#abyss', { opacity: 0, duration: 5 }, 63)
      .to('#fog-a', { opacity: 0.15, duration: 6 }, 63)
      .to('#fog-b', { opacity: 0.1, duration: 6 }, 63)
      .to('#dawn-glow', { opacity: 0, duration: 4 }, 63)
      .to(mood, { v: 2, duration: 7, onUpdate: breathe }, 64)
      .to('#deck-glow', { opacity: 0.35, duration: 4 }, 64)
      .to('#sky-night', { opacity: 1, duration: 6 }, 64.5)
      .to('#clouds', { opacity: 0.55, duration: 3 }, 65)
      .to('#birds', { opacity: 0, duration: 2 }, 65)
      .to('#travelers, #ripples, #flows', { opacity: 0, duration: 2 }, 66)
      .to('#stars', { opacity: 0.9, duration: 5 }, 66.5)
      .to('#aurora', { opacity: 0.6, duration: 5 }, 68.5)

    // — chapter IV (74–88): the eye opens, the world becomes one
    tl.to('#ring-outer', { rotation: 26, duration: 14 }, 74)
      .to('#ring-rete', { rotation: -34, duration: 14 }, 74)
      .to('#iris', { rotation: 40, duration: 14 }, 74)
      .to('#eye-lids .t-draw', { strokeDashoffset: 0, duration: 3, stagger: 0.4 }, 74.5)
      .to('#constellations .t-draw', { strokeDashoffset: 0, duration: 4, stagger: 0.8 }, 75.5)
      .fromTo('#eye-core', { opacity: 0, scale: 0.8 }, { opacity: 1, scale: 1, duration: 3 }, 76)
      .to('#aurora', { opacity: 0.85, duration: 4 }, 76)
      .to('#obs-waves', { opacity: 0.4, duration: 2 }, 77.5)
      .to('#rays', { opacity: 0.85, duration: 1.5 }, 78.5)
      .to('#rays .t-draw', { strokeDashoffset: 0, duration: 4.5, stagger: 0.35 }, 78.5)
      .to('#clouds', { opacity: 0.3, duration: 4 }, 80)

    // — the ending (89–100): pull back — it was one landscape all along
    // narrow screens crop the 1440-wide frame hard, so the camera also
    // pans: wider on the rebuild, centered on the eye for the summit
    const narrow = window.innerWidth < 768
    if (narrow) {
      tl.to('#world', { scale: 0.8, duration: 8, ease: 'power1.inOut' }, 45)
        .to('#world', { x: -300, scale: 0.85, duration: 9 }, 63)
        .to('#world', { x: -120, scale: 0.8, duration: 9, ease: 'power1.inOut' }, 89.5)
    } else {
      tl.to('#world', { scale: 0.94, duration: 9, ease: 'power1.inOut' }, 89.5)
    }
    tl.to('#ridge-far', { y: 70, duration: 8, ease: 'power1.inOut' }, 89.5)
      .to('#ridge-mid', { y: 130, duration: 8, ease: 'power1.inOut' }, 89.5)
      .to('#cliff-left, #cliff-right, #bridge', { y: 240, duration: 8, ease: 'power1.inOut' }, 89.5)
      .to('#tree', { y: 250, duration: 8, ease: 'power1.inOut' }, 89.5)
      .to('#fg', { y: 420, duration: 8, ease: 'power1.inOut' }, 89.5)
      .to('#abyss', { opacity: 0.5, duration: 4 }, 90)
      .to('#threads', { opacity: 0.9, duration: 1.5 }, 91)
      .to('#threads .t-draw', { strokeDashoffset: 0, duration: 4, stagger: 0.4 }, 91)
      .to('#fog-b', { opacity: 0.18, duration: 4 }, 92)
      .to('#deck-glow', { opacity: 0.85, duration: 3 }, 93)
      .to('#rays', { opacity: 0.5, duration: 3 }, 93)
      .to('#flows', { opacity: 0.8, duration: 2 }, 93.5)
      .to('#tree-sparks', { opacity: 0.9, duration: 2 }, 93.5)
      .to('#far-lights', { opacity: 1, duration: 2 }, 94)

    // ---- ambient life, gated by where the journey stands -------------
    const floatLoops = []
    const lifeLoops = []
    const netLoops = []

    gsap.utils.toArray('.bob').forEach((el, i) => {
      floatLoops.push(
        gsap.to(el, {
          y: `+=${7 + (i % 3) * 3}`,
          rotation: i % 2 ? 5 : -5,
          duration: 2.8 + (i % 5) * 0.6,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut',
          paused: true
        })
      )
    })

    gsap.utils.toArray('.traveler').forEach((el, i) => {
      const walk = gsap.to(el, {
        motionPath: { path: '#deck-path', align: '#deck-path', alignOrigin: [0.5, 1] },
        duration: 16,
        repeat: -1,
        ease: 'none',
        paused: true
      })
      walk.progress(i / 3)
      lifeLoops.push(walk)
    })
    ;['#ripple-a', '#ripple-b'].forEach((sel, i) => {
      const ripple = gsap.fromTo(
        sel,
        { attr: { r: 5 }, opacity: 0.5 },
        { attr: { r: 85 }, opacity: 0, duration: 3.4, repeat: -1, ease: 'power1.out', paused: true }
      )
      ripple.progress(i * 0.5)
      lifeLoops.push(ripple)
    })
    gsap.utils.toArray('.bird').forEach((el, i) => {
      const flight = gsap.to(el, {
        motionPath: { path: '#bird-path', align: '#bird-path', alignOrigin: [0.5, 0.5] },
        duration: 34,
        repeat: -1,
        ease: 'none',
        paused: true
      })
      flight.progress(i * 0.28)
      lifeLoops.push(flight)
    })

    const pulse = (dots, pathPrefix, base, sink) => {
      gsap.utils.toArray(dots).forEach((el, i) => {
        const dur = base + i * 0.9
        sink.push(
          gsap.to(el, {
            motionPath: { path: `${pathPrefix}${i}`, align: `${pathPrefix}${i}`, alignOrigin: [0.5, 0.5] },
            duration: dur,
            repeat: -1,
            ease: 'power1.inOut',
            paused: true
          }),
          gsap.to(el, {
            keyframes: { opacity: [0, 0.9, 0.9, 0] },
            duration: dur,
            repeat: -1,
            ease: 'none',
            paused: true
          })
        )
      })
    }
    pulse('.ray-dot', '#ray-', 3.6, netLoops)
    pulse('.thread-dot', '#thread-', 5.2, netLoops)

    const gate = (loops, on) =>
      loops.forEach((l) => (on ? l.paused() && l.play() : !l.paused() && l.pause()))
    gate(floatLoops, true)

    ScrollTrigger.create({
      trigger: wrapRef.value,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate(trigger) {
        const p = trigger.progress
        gate(floatLoops, p < 0.45)
        gate(lifeLoops, p > 0.55 && p < 0.72)
        gate(netLoops, p > 0.77)
      }
    })
  }, wrapRef.value)
})

// prefers-reduced-motion: compose the final wide shot — the whole
// journey, already connected — and let the story be read in peace
function setFinalState(gsap) {
  gsap.set('.stone', { x: 0, y: 0, rotation: 0 })
  gsap.set('.shard', { opacity: 0 })
  gsap.set('.t-draw, #deck-path', { strokeDashoffset: 0 })
  gsap.set('.t-leaf', { opacity: 1 })
  gsap.set('#world', window.innerWidth < 768 ? { scale: 0.8, x: -120 } : { scale: 0.94 })
  gsap.set('#ridge-far', { y: 70 })
  gsap.set('#ridge-mid', { y: 130 })
  gsap.set('#cliff-left, #cliff-right, #bridge', { y: 240 })
  gsap.set('#tree', { y: 250 })
  gsap.set('#fg', { y: 420 })
  gsap.set('#summit', { y: 0 })
  gsap.set('#abyss', { opacity: 0.5 })
  gsap.set('#stars', { opacity: 0.9 })
  gsap.set('#aurora', { opacity: 0.8 })
  gsap.set('#eye-core', { opacity: 1 })
  gsap.set('#obs-waves', { opacity: 0.3 })
  gsap.set('#clouds', { opacity: 0.3 })
  gsap.set('#rays', { opacity: 0.5 })
  gsap.set('#threads', { opacity: 0.9 })
  gsap.set('#deck-glow', { opacity: 0.85 })
  gsap.set('#lanterns, #village-lights', { opacity: 1 })
  gsap.set('#far-lights', { opacity: 1 })
  gsap.set('#tree-sparks', { opacity: 0.9 })
  gsap.set('#flows', { opacity: 0.8 })
  gsap.set('#sky-warm', { opacity: 0 })
  gsap.set('#sky-night', { opacity: 1 })
  gsap.set('#fog-a', { opacity: 0.15 })
  gsap.set('#fog-b', { opacity: 0.18 })
  gsap.set('#scroll-hint', { opacity: 0 })
}

onUnmounted(() => {
  ashRef.value?.stop()
  gsapCtx?.revert()
})
</script>

<template>
  <div ref="wrapRef" class="relative bg-[#07051a] text-white">
    <!-- ============ THE WORLD (sticky, lives through the whole saga) ============ -->
    <div class="sticky top-0 h-screen supports-[height:100svh]:h-[100svh] overflow-hidden" aria-hidden="true">
      <!-- three skies, crossfaded by the journey -->
      <div
        id="sky-cold"
        class="absolute inset-0"
        style="background: linear-gradient(180deg, #05040f 0%, #0b0d1f 45%, #141a30 78%, #0e1224 100%)"
      ></div>
      <div
        id="sky-warm"
        class="absolute inset-0 opacity-0"
        style="
          background:
            radial-gradient(90% 60% at 72% 68%, rgba(240, 167, 92, 0.14) 0%, transparent 60%),
            linear-gradient(180deg, #0a0620 0%, #221443 42%, #3a2a63 76%, #331f4e 100%);
        "
      ></div>
      <div
        id="sky-night"
        class="absolute inset-0 opacity-0"
        style="background: linear-gradient(180deg, #020109 0%, #090617 48%, #140d33 100%)"
      ></div>

      <SagaWorld class="absolute inset-0" />

      <!-- fog banks, breathing across the valley -->
      <div id="fog-a" class="fog absolute -inset-x-[12%] top-[32%] h-[46%]" style="opacity: 0.85"></div>
      <div id="fog-b" class="fog fog-rev absolute -inset-x-[12%] top-[56%] h-[40%]" style="opacity: 0.6"></div>

      <!-- ash / motes / stardust -->
      <AshField ref="ashRef" class="absolute inset-0" />

      <!-- the fracture dims the world -->
      <div id="story-dim" class="absolute inset-0 bg-[#04020d] opacity-0"></div>

      <!-- vignette keeps the copy readable -->
      <div
        class="absolute inset-0"
        style="background: radial-gradient(120% 100% at 50% 42%, transparent 50%, rgba(3, 2, 10, 0.62) 100%)"
      ></div>
    </div>

    <!-- ============ THE STORY (scrolls over the world) ============ -->
    <div class="relative z-10 -mt-[100vh] supports-[height:100svh]:-mt-[100svh]">
      <!-- prologue -->
      <header class="story-block min-h-screen supports-[height:100svh]:min-h-[100svh] flex items-center justify-center text-center px-6">
        <div class="max-w-3xl">
          <p class="reveal text-xs font-mono text-[#b7a3f6] tracking-[0.3em] uppercase mb-6">
            {{ $t('philosophy.hero.eyebrow') }}
          </p>
          <h1 class="reveal text-5xl md:text-7xl font-semibold leading-[1.06] rtl:leading-[1.4]">
            {{ $t('philosophy.hero.title') }}
          </h1>
          <p class="reveal mt-8 text-lg md:text-xl text-white/55 leading-relaxed">
            {{ $t('philosophy.hero.sub') }}
          </p>
          <div id="scroll-hint" class="mt-16 flex flex-col items-center gap-2" aria-hidden="true">
            <span class="scroll-line block w-px h-14 bg-gradient-to-b from-transparent via-[#b7a3f6]/60 to-[#f0a75c]/70"></span>
          </div>
        </div>
      </header>

      <!-- chapters I → IV -->
      <template v-for="(ch, idx) in CHAPTERS" :key="idx">
        <div :class="ch.spacer" aria-hidden="true"></div>
        <section class="story-block flex items-center px-6" :class="ch.minH">
          <div class="w-full max-w-6xl mx-auto">
            <div
              :class="
                ch.align === 'center'
                  ? 'max-w-2xl mx-auto text-center'
                  : ch.align === 'start'
                    ? 'max-w-xl me-auto'
                    : 'max-w-xl ms-auto'
              "
            >
              <!-- product reveal — the official Heimdall lockup, chapter IV only -->
              <div
                v-if="idx === 3"
                class="reveal inline-flex items-center gap-3 mb-8 px-4 py-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-sm shadow-[0_0_30px_-6px_rgba(215,95,232,0.35)]"
              >
                <svg viewBox="0 0 40 24" fill="none" class="w-8 h-5" aria-hidden="true">
                  <path d="M3 12 Q20 1 37 12 Q20 23 3 12 Z" stroke="#b7a3f6" stroke-width="1.6" />
                  <circle cx="20" cy="12" r="5.5" stroke="#d75fe8" stroke-width="1.2" />
                  <circle cx="20" cy="12" r="1.8" fill="#ffd9a2" />
                </svg>
                <span class="font-heading font-semibold text-sm tracking-tight">{{ $t('philosophy.heimdall.badge') }}</span>
                <span class="hidden sm:inline w-px h-4 bg-white/20" aria-hidden="true"></span>
                <span class="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
                  {{ $t('philosophy.heimdall.tagline') }}
                </span>
              </div>

              <p
                class="reveal flex items-center gap-3 text-xs font-mono text-[#f0b96b] tracking-[0.3em] uppercase mb-6"
                :class="ch.align === 'center' ? 'justify-center' : ''"
              >
                <svg viewBox="0 0 28 32" class="w-3.5 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                  <path v-for="(d, j) in RUNES[idx]" :key="j" :d="d" />
                </svg>
                <span>{{ $t(`philosophy.sections[${idx}].eyebrow`) }}</span>
              </p>
              <h2 class="reveal text-4xl md:text-6xl font-semibold leading-[1.08] rtl:leading-[1.4] mb-10">
                {{ $t(`philosophy.sections[${idx}].title`) }}
              </h2>
              <p class="reveal text-lg md:text-xl text-white/60 leading-relaxed mb-6">
                {{ $t(`philosophy.sections[${idx}].p1`) }}
              </p>
              <p class="reveal text-lg md:text-xl text-white/60 leading-relaxed">
                {{ $t(`philosophy.sections[${idx}].p2`) }}
              </p>
              <div class="reveal mt-12" :class="ch.align === 'center' ? '' : 'text-start'">
                <span class="block w-10 h-px mb-5 bg-gradient-to-r from-[#f0a75c]/70 to-transparent" :class="ch.align === 'center' ? 'mx-auto bg-gradient-to-r from-transparent via-[#f0a75c]/70 to-transparent' : ''" aria-hidden="true"></span>
                <p class="text-xl md:text-2xl font-medium italic text-transparent bg-clip-text bg-[linear-gradient(95deg,#ffe7bd_0%,#ffb45c_50%,#ef77dd_110%)]">
                  «&nbsp;{{ $t(`philosophy.sections[${idx}].quote`) }}&nbsp;»
                </p>
              </div>
            </div>
          </div>
        </section>
      </template>

      <div class="h-[20vh]" aria-hidden="true"></div>

      <!-- the vision — earned, at the top of the world -->
      <section class="story-block min-h-[130vh] flex items-center justify-center text-center px-6">
        <div class="max-w-3xl pb-16">
          <h2 class="reveal text-5xl md:text-7xl font-semibold leading-[1.06] rtl:leading-[1.4] mb-8">
            {{ $t('philosophy.vision.title') }}
          </h2>
          <p class="reveal text-lg md:text-xl text-white/55 leading-relaxed mb-12">
            {{ $t('philosophy.vision.desc') }}
          </p>
          <div class="reveal flex flex-col sm:flex-row justify-center gap-3">
            <a
              href="https://bifrost.events/touch"
              target="_blank"
              class="px-8 py-4 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition shadow-[0_0_40px_-8px_rgba(255,180,92,0.5)]"
            >
              {{ $t('philosophy.vision.cta') }}
            </a>
            <router-link
              :to="localPath('/platform')"
              class="px-8 py-4 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition"
            >
              {{ $t('philosophy.vision.secondary') }}
            </router-link>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/* fog banks — soft gradients drifting on the compositor only */
.fog {
  background: radial-gradient(55% 90% at 50% 50%, rgba(126, 136, 178, 0.3) 0%, rgba(126, 136, 178, 0.12) 45%, transparent 72%);
  filter: blur(26px);
  animation: fog-drift 19s ease-in-out infinite alternate;
  will-change: transform;
  pointer-events: none;
}

.fog-rev {
  background: radial-gradient(60% 90% at 50% 55%, rgba(104, 112, 152, 0.26) 0%, rgba(104, 112, 152, 0.1) 48%, transparent 74%);
  animation: fog-drift 27s ease-in-out -9s infinite alternate-reverse;
}

@keyframes fog-drift {
  from { transform: translateX(-3.5%); }
  to { transform: translateX(3.5%); }
}

/* the hero's quiet invitation */
.scroll-line {
  transform-origin: top;
  animation: scroll-breathe 2.6s ease-in-out infinite;
}

@keyframes scroll-breathe {
  0%, 100% { transform: scaleY(0.35); opacity: 0.4; }
  55% { transform: scaleY(1); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fog,
  .fog-rev,
  .scroll-line {
    animation: none;
  }
}
</style>
